import * as React from 'react'

import { makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'

import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import PermitCompactHeaderBar from '~/client/src/shared/components/CompactHeaderBar/components/PermitCompactHeaderBar'
import FileInputBase from '~/client/src/shared/components/FileInput/FileInput'
import PermitFormLoader from '~/client/src/shared/components/SitePermitCreationForm/components/PermitFormLoader'
import SitePermitCreationFormContent from '~/client/src/shared/components/SitePermitCreationForm/SitePermitCreationFormContent'
import SitePermitCreationFormStore from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm.store'
import { IAppConfig } from '~/client/src/shared/Config'
import Workflow from '~/client/src/shared/models/Workflow'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '~/client/src/shared/stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import FormDeleteConfirm from './components/FormDeleteConfirm'
import WorkflowMoreMenu from './components/WorkflowMoreMenu'
import WorkflowSubmissionButtons from './components/WorkflowSubmissionButtons'
import FormReportGenerationStore from './FormReportGeneration.store'

import './SitePermitCreationForm.scss'

// localization: no text to translate

interface IProps {
  draftWorkflow: Workflow
  FileInputType: typeof FileInputBase

  close: () => void
  onDuplicateClick: (workflow: Workflow) => void
  openWorkflow: (workflow: Workflow) => void

  projectDateStore?: ProjectDateStore
  eventsStore?: EventsStore
  fileUploadingStore?: FileUploadingStore
  workflowTypesStore?: WorkflowTypesStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  tagsStore?: TagsStore
  graphExecutorStore?: GraphExecutorStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  sitemapItemsStore?: SitemapItemsStore
  workflowsStore?: WorkflowsStore
  configuration?: IAppConfig
  sitemapsStore?: SitemapsStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
}

@inject(
  'projectDateStore',
  'eventsStore',
  'fileUploadingStore',
  'workflowTypesStore',
  'permitStatusChangesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'userProjectsStore',
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'calendarPlaceholderStore',
  'tagsStore',
  'graphExecutorStore',
  'recurringWorkflowSettingsStore',
  'sitemapItemsStore',
  'workflowsStore',
  'configuration',
  'sitemapsStore',
  'closuresStore',
  'markupsStore',
)
@observer
export default class WorkflowDraftForm extends React.Component<IProps> {
  private readonly store: SitePermitCreationFormStore
  private readonly formReportGenerationStore: FormReportGenerationStore

  public constructor(props: IProps) {
    super(props)

    this.store = new SitePermitCreationFormStore(
      props.eventsStore,
      props.projectDateStore,
      props.fileUploadingStore,
      props.workflowTypesStore,
      props.userProjectsStore,
      props.projectMembersStore,
      props.companiesStore,
      props.locationAttributesStore,
      props.materialsStore,
      props.materialCategoryStore,
      props.tagsStore,
      props.graphExecutorStore,
      props.closuresStore,
      props.markupsStore,
      props.workflowsStore,
      props.permitStatusChangesStore,
      props.permitInspectionsStore,
      props.permitInspectionChangesStore,
      props.calendarPlaceholderStore,
      props.recurringWorkflowSettingsStore,
      props.sitemapItemsStore,
    )

    this.formReportGenerationStore = new FormReportGenerationStore(
      props.projectDateStore,
      props.eventsStore,
      props.configuration,
      props.sitemapItemsStore,
      props.sitemapsStore,
      props.locationAttributesStore,
      props.projectMembersStore,
      props.workflowTypesStore,
      props.companiesStore,
      props.materialsStore,
      props.userProjectsStore,
    )

    this.store.resetSelectedField()
    this.store.setDefaultSitePermit(props.draftWorkflow)
    this.store.setOpenWorkflowHandler(props.openWorkflow)

    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const { draftWorkflow } = this.props

    if (draftWorkflow === prevProps.draftWorkflow) {
      return
    }

    const {
      inspectionFieldsStore,
      setDefaultSitePermit,
      hideNonWorkTimeLabel,
      unblockSubmitButton,
      markPermitAsNonWorkTimeIfNeed,
      resetSelectedField,
      workflowFieldsStore,
    } = this.store

    inspectionFieldsStore.deselectInspection()
    unblockSubmitButton()
    hideNonWorkTimeLabel()

    resetSelectedField()
    setDefaultSitePermit(draftWorkflow)

    markPermitAsNonWorkTimeIfNeed()
    workflowFieldsStore.expandAllSections()
  }

  public render() {
    const { FileInputType, workflowTypesStore, close, openWorkflow } =
      this.props
    const {
      existingPermit,
      template,
      canSaveWorkflow,
      isDraftButtonEnabled,
      isDeleteConfirmModalOpen,
      isWorkflowCreator,
      toggleDeleteConfirmModal,
      deleteForm,
      saveDraftAsRequested,
      saveDraftWorkflow,
    } = this.store

    return (
      <div className="site-permit-creation-form full-height relative">
        <div className="site-permit-creation-form-content full-height col">
          <PermitCompactHeaderBar
            permit={existingPermit}
            workflowTypesStore={workflowTypesStore}
            onCloseClick={close}
            menuEl={this.workflowMoreMenu}
            openWorkflow={openWorkflow}
          />
          <div className="full-height col overflow-hidden relative">
            <FormDeleteConfirm
              isDraftWorkflow
              isShown={isDeleteConfirmModalOpen}
              isRecurringWorkflow={false}
              onHide={toggleDeleteConfirmModal}
              onApply={thisAndFuture => deleteForm(thisAndFuture, close)}
            />
            <PermitFormLoader permitCreationStore={this.store} />
            <SitePermitCreationFormContent
              isTypeReadOnly
              shouldHideResponsible
              store={this.store}
              isReadonly={!isWorkflowCreator}
              FileInputType={FileInputType}
              onPermitClose={close}
            />
            {template && isWorkflowCreator && (
              <WorkflowSubmissionButtons
                isDraftBtnEnabled={isDraftButtonEnabled}
                isSubmitBtnEnabled={canSaveWorkflow}
                onDraftClick={() => saveDraftWorkflow(close)}
                onSubmitClick={saveDraftAsRequested}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  private get workflowMoreMenu(): JSX.Element {
    const { onDuplicateClick, workflowsStore } = this.props

    return (
      <WorkflowMoreMenu
        store={this.store}
        isDisabled={this.store.isLoading || workflowsStore?.isDeleting}
        onReportSubmitClick={
          this.formReportGenerationStore.generateAndSendReport
        }
        onDuplicateClick={onDuplicateClick}
      />
    )
  }
}
